import { Grid, Typography } from '@mui/material'
import {
  getActiveRegistrationsListingPriceSum,
  getCommercialDiscount,
  getDpcDisplayedFormationPrice,
  getPersonalFundingDue,
  getTotalCompensation,
  getTotalPartOrga,
  hasRealCommercialDiscount,
  resolveDpcNetCompensation,
} from '../utils/pricing'
import { FormType } from '../utils/sharedTypes'

type Props = {
  form: FormType
}

export default function FundingSummaryDPC({ form }: Props) {
  // NB: seuil > 1 (et non > 0) volontaire ici : uniquement pour le texte affiché
  // (singulier/pluriel), à ne pas confondre avec hasMultipleProducts de pricing.ts.
  const hasProducts = form.data.products.length > 1 && form.data.product === null
  const listing_price = getActiveRegistrationsListingPriceSum(form.data)

  const personalFunding = getPersonalFundingDue(form.data)
  const totalPartOrga = getTotalPartOrga(form.data)
  const totalCompensation = getTotalCompensation(form.data)

  const commercialDiscount = getCommercialDiscount(form.data)
  const hasCommercialDiscount = hasRealCommercialDiscount(form.data)
  const displayedFormationPrice = getDpcDisplayedFormationPrice(form.data)
  const displayedCompensation = resolveDpcNetCompensation(totalCompensation, personalFunding)

  return (
    <Grid item container spacing={1}>
      <Grid item container direction="row" justifyContent="space-between" wrap="nowrap">
        <Grid item sx={{ maxWidth: '50%' }}>
          <Typography variant="body2">Prix {hasProducts ? 'total de mon parcours de' : 'de ma'} formation</Typography>
        </Grid>
        <Typography variant="body1" sx={{ textAlign: 'right', fontWeight: 400 }}>
          {Intl.NumberFormat('fr-FR', {
            style: 'currency',
            currency: 'EUR',
            maximumFractionDigits: 2,
          }).format(displayedFormationPrice) || '-'}
        </Typography>
      </Grid>

      <Grid item container direction="row" justifyContent="space-between" wrap="nowrap">
        <Grid item sx={{ maxWidth: '50%' }}>
          <Typography variant="body2">Montant {hasProducts ? 'total' : ''} pris en charge par l'ANDPC</Typography>
        </Grid>

        <Typography variant="body1" sx={{ textAlign: 'right', fontWeight: 400 }}>
          {Intl.NumberFormat('fr-FR', {
            style: 'currency',
            currency: 'EUR',
            maximumFractionDigits: 2,
          }).format(
            totalPartOrga ??
              form.data.product_catalog?.funding_price ??
              form.data.product_catalog?.listing_price ??
              listing_price,
          ) || '-'}
        </Typography>
      </Grid>

      {hasCommercialDiscount && (
        <Grid item container direction="row" justifyContent="space-between" wrap="nowrap">
          <Grid item sx={{ maxWidth: '50%' }}>
            <Typography variant="body2">Remise commerciale</Typography>
          </Grid>
          <Typography variant="body1" sx={{ textAlign: 'right', fontWeight: 400 }}>
            {Intl.NumberFormat('fr-FR', {
              style: 'currency',
              currency: 'EUR',
              maximumFractionDigits: 2,
            }).format(commercialDiscount) || '-'}
          </Typography>
        </Grid>
      )}

      <Grid
        item
        container
        direction={{ md: 'row', xs: 'column' }}
        justifyContent="space-around"
        alignItems={{ xs: 'center', md: 'end' }}
        wrap="nowrap"
        mt={3}
      >
        <Grid item>
          <Typography variant="body1" sx={{ textAlign: 'center', fontSize: 18, fontWeight: 500 }}>
            À payer maintenant
          </Typography>
          <Typography variant="body1" sx={{ textAlign: 'center', fontSize: 25, fontWeight: 500 }}>
            {Intl.NumberFormat('fr-FR', {
              style: 'currency',
              currency: 'EUR',
              maximumFractionDigits: 2,
            }).format(
              form.data.personal_funding !== null
                ? form.data.personal_funding
                : form.data.default_personal_funding ?? 0,
            )}
          </Typography>
        </Grid>
        {totalCompensation > 0 && (
          <Grid>
            <Typography variant="body1" sx={{ textAlign: 'center', fontSize: 18, fontWeight: 500 }}>
              Vos indemnités {totalCompensation - personalFunding > 0 && 'nettes'}
              <sup>*</sup>
            </Typography>
            <Typography variant="body1" sx={{ textAlign: 'center', fontSize: 25, fontWeight: 500 }}>
              {Intl.NumberFormat('fr-FR', {
                style: 'currency',
                currency: 'EUR',
                maximumFractionDigits: 2,
              }).format(displayedCompensation)}
            </Typography>
          </Grid>
        )}
      </Grid>
      {totalCompensation > 0 && (
        <Grid container justifyContent="center">
          <Typography variant="caption" textAlign="center" mt={2}>
            <sup>*</sup>
            {totalCompensation - personalFunding > 0 ? (
              <>
                Estimation des indemnités perçues en cas de validation de{' '}
                {hasProducts ? `vos ${form.data.products.length} formations` : 'votre formation'}
                {personalFunding > 0 && " après déduction du montant payé aujourd'hui"}
              </>
            ) : (
              <>
                Estimation des indemnités perçues en cas de validation de{' '}
                {hasProducts ? `vos ${form.data.products.length} formations` : 'votre formation'}
              </>
            )}
          </Typography>
        </Grid>
      )}
    </Grid>
  )
}
